import { Button, MenuItem, Paper, Select, Typography } from '@dls/react-core';
import { Icon } from '@dls/react-icon';
import React, { BaseSyntheticEvent, useMemo, useState } from 'react';
import styled from 'styled-components';
import StudyCard from '../../components/studies/study-card';
import { Study } from '../../api/studykit';
import { Grid } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../routes/main-routes';
import { NoDataMessage } from '../../components/no-data-message';
import { HasPermission } from '../../context/authorization';

const StyledHeadingContainer = styled.div`
  display: flex;
  flex: 1;
  background-color: white;
  justify-content: space-between;
`;

const StyledControlsContainer = styled.span`
  display: inline-flex;
`;

const SORTING_DIRECTIONS = ['Alphabetically', 'Start date', 'End date'] as const;
type SortingDirection = typeof SORTING_DIRECTIONS[number];

interface StudiesPanelContentsProps {
  studies: Study[];
  status: string;
  title: string;
}

const StudiesPanelContents: React.FC<StudiesPanelContentsProps> = ({ studies, title, status }) => {
  const navigate = useNavigate();
  const [sortingDirection, setSortingDirection] = useState<SortingDirection>(SORTING_DIRECTIONS[0]);
  const sortedStudies = useMemo(() => {
    return [...studies].sort((a, b) => {
      switch (sortingDirection) {
        case 'Alphabetically':
          return a.name.localeCompare(b.name);
        case 'End date':
          return new Date(a.end).valueOf() - new Date(b.end).valueOf();
        case 'Start date':
          return new Date(a.start).valueOf() - new Date(b.start).valueOf();
        default:
          throw new Error('unknown sorting direction');
      }
    });
  }, [sortingDirection, studies]);

  return (
    <Paper>
      <StyledHeadingContainer>
        <Typography variant="h2" color="secondary">
          {title}
        </Typography>
        <StyledControlsContainer>
          <Select
            style={{ marginRight: `8px`, width: `12rem` }}
            className={'DLSDropdown'}
            value={sortingDirection}
            onChange={(e: BaseSyntheticEvent) => setSortingDirection(e.target.value)}
          >
            {SORTING_DIRECTIONS.map((e) => (
              <MenuItem key={e} value={e}>
                <span>{e}</span>
              </MenuItem>
            ))}
          </Select>
          <HasPermission value="Study.Create">
            <Button
              style={{ width: `12rem` }}
              dls_variant="primary"
              onClick={() => navigate(`/${RoutePaths.AddStudy}`)}
              startIcon={<Icon name="Plus" color="white" size={'m'} />}
            >
              Add study
            </Button>
          </HasPermission>
        </StyledControlsContainer>
      </StyledHeadingContainer>
      <Grid container spacing={4} style={{ width: `100%` }}>
        {sortedStudies.length !== 0 ? (
          sortedStudies.map((e) => (
            <Grid key={e.id} item xs={12} lg={6} xl={4}>
              <StudyCard study={e} />
            </Grid>
          ))
        ) : (
          <Paper style={{ padding: `1em`, width: '100%' }}>
            <NoDataMessage
              title={`There are no ${status} studies yet`}
              subtitle={
                status === 'ongoing' ? (
                  <>
                    You currently aren’t assigned to any studies.
                    <br />
                    <br />
                    Click on the Add study button in order to create a new study or <br /> contact
                    the study manager to add you to their study.
                  </>
                ) : undefined
              }
            />
          </Paper>
        )}
      </Grid>
    </Paper>
  );
};

export default StudiesPanelContents;
